import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, TrendingUp } from "lucide-react";

/**
 * ROI calculator — Luminous Atrium styling.
 * Sources:
 *  - Work Institute 2020 Retention Report: avg replacement cost ≈ 33% of annual salary.
 *  - Gallup / SHRM: voluntary turnover ≈ 13%/yr; ~half is "regrettable" (~6.5%).
 *  - Conservative assumption: early honest feedback prevents ~15% of regrettable
 *    departures → ≈ 1 prevented per 100 employees per year.
 */
export const EditorialROICalculator = () => {
  const navigate = useNavigate();
  const [headcount, setHeadcount] = useState(120);
  const [salary, setSalary] = useState(70000);

  const { saved, prevented } = useMemo(() => {
    const preventedDepartures = Math.max(1, Math.round(headcount / 100));
    const costPerDeparture = salary * 0.33;
    return {
      prevented: preventedDepartures,
      saved: Math.round((preventedDepartures * costPerDeparture) / 1000) * 1000,
    };
  }, [headcount, salary]);

  const fmt = (n: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(n);

  return (
    <section
      id="roi"
      aria-labelledby="roi-title"
      className="relative bg-background border-t border-border/60 px-4 sm:px-6 md:px-10 py-16 sm:py-20 md:py-28 overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-0 opacity-70"
        style={{
          background:
            "radial-gradient(900px 500px at 90% 0%, hsl(50 60% 92% / 0.55), transparent 60%), radial-gradient(700px 420px at 0% 100%, hsl(150 30% 90% / 0.45), transparent 60%)",
        }}
      />

      <div className="max-w-7xl mx-auto relative">
        <div className="flex items-center justify-center mb-8 md:mb-10">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 backdrop-blur px-3.5 py-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-medium text-foreground/70 shadow-[0_2px_8px_-4px_rgba(21,34,27,0.06)]">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            ROI Calculator
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <h2
              id="roi-title"
              className="font-sans font-medium text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.05] tracking-[-0.02em] text-foreground mb-5 md:mb-7 text-center lg:text-left"
            >
              One prevented resignation,{" "}
              <span className="font-serif italic font-light text-foreground/75">
                paid for the year.
              </span>
            </h2>
            <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-8 md:mb-10 text-center lg:text-left mx-auto lg:mx-0">
              The Work Institute pegs the average replacement cost of a departing employee at{" "}
              <span className="text-foreground font-medium">
                ~33% of annual salary
              </span>
              . Move the dials to see what catching one regrettable resignation early is worth on your team.
            </p>

            <div className="rounded-3xl bg-card/90 backdrop-blur-sm border border-border/50 p-6 sm:p-8 shadow-[0_8px_24px_-12px_rgba(21,34,27,0.06)]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                <label className="block">
                  <div className="flex items-baseline justify-between mb-3">
                    <span className="text-xs font-medium text-muted-foreground tracking-[-0.01em]">
                      Headcount
                    </span>
                    <span className="font-sans text-lg font-medium text-foreground tabular-nums">
                      {headcount}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={20}
                    max={400}
                    step={10}
                    value={headcount}
                    onChange={(e) => setHeadcount(Number(e.target.value))}
                    className="w-full accent-primary cursor-pointer h-2"
                    aria-label="Headcount"
                  />
                </label>
                <label className="block">
                  <div className="flex items-baseline justify-between mb-3">
                    <span className="text-xs font-medium text-muted-foreground tracking-[-0.01em]">
                      Avg. salary
                    </span>
                    <span className="font-sans text-lg font-medium text-foreground tabular-nums">
                      {fmt(salary)}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={40000}
                    max={200000}
                    step={5000}
                    value={salary}
                    onChange={(e) => setSalary(Number(e.target.value))}
                    className="w-full accent-primary cursor-pointer h-2"
                    aria-label="Average salary"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Result card */}
          <aside className="lg:col-span-5 rounded-3xl bg-card border border-border/50 p-6 sm:p-8 md:p-10 shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)] transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <TrendingUp className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </span>
              <span className="text-xs font-medium text-muted-foreground tracking-[-0.01em]">
                Estimated annual savings
              </span>
            </div>
            <div className="font-sans font-medium text-5xl sm:text-6xl md:text-[72px] leading-[1.02] tracking-[-0.02em] text-foreground mb-5 md:mb-6 tabular-nums">
              {fmt(saved)}
            </div>
            <p className="font-sans text-sm md:text-base text-muted-foreground leading-relaxed border-t border-border/60 pt-5">
              From preventing just{" "}
              <span className="font-medium text-foreground">{prevented}</span>{" "}
              regrettable departure{prevented > 1 ? "s" : ""} per year on a {headcount}-person team.
              SendFeedback starts at{" "}
              <span className="font-medium text-foreground">$39/mo</span>.
            </p>
            <button
              onClick={() => navigate("/auth?mode=signup")}
              className="group mt-7 inline-flex items-center justify-center gap-2.5 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium hover:shadow-[0_12px_36px_rgba(62,92,77,0.36)] hover:-translate-y-0.5 transition-all duration-300 min-h-[52px] w-full sm:w-auto"
            >
              Try free for 15 days
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </aside>
        </div>
      </div>
    </section>
  );
};
